import { useQuery } from "@tanstack/react-query";
import { getLocations } from "@coworker/apprestructured/src/task-management/services/locations.service";
import { useMemo } from "react";
import {
  GroupedSalesLocations,
  GroupedSalesLocationsDTO,
  HomeAndPrimarySalesLocation,
  SalesLocation,
  SalesLocationDTO,
} from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { useTranslation } from "@coworker/locales";
import { getDepartmentNameFromCode } from "@coworker/apprestructured/src/task-management/utils/departmentName";

const SFM_FALLBACK_LOCATION: HomeAndPrimarySalesLocation = {
  slid: "SFM",
  assignedSalesSpaceQuantity: null,
  specialityShopName: "Swedish Food Market",
  departmentName: "Swedish Food Market",
  isHome: true,
  isPrimary: true,
};

const matchesSearchQuery = (
  location: SalesLocation,
  query: string
): boolean => {
  if (!query) return true;
  // Query is expected to be already trimmed and lowercased
  return (
    location.slid.toLowerCase().includes(query) ||
    location.specialityShopName.toLowerCase().includes(query) ||
    location.departmentName.toLowerCase().includes(query)
  );
};

const filterLocations = <T extends SalesLocation>(
  locations: T[],
  query: string
): T[] => locations.filter((loc) => matchesSearchQuery(loc, query));

const calculateTotalQuantity = (
  locations: (SalesLocationDTO | SalesLocation)[]
): number =>
  locations.reduce(
    (sum, loc) => sum + (loc.assignedSalesSpaceQuantity || 0),
    0
  );

// Generic transformation function that works for both SalesLocationDTO and HomeAndPrimarySalesLocationDTO
// Falls back to specialityShopName if departmentFromSlid is not provided (gradual migration support)
const enrichLocation = <T extends SalesLocationDTO>(
  location: T,
  t: (key: string, options?: any) => string
): Omit<T, "departmentFromSlid"> & { departmentName: string } => {
  const { departmentFromSlid, ...rest } = location;
  return {
    ...rest,
    departmentName: departmentFromSlid
      ? getDepartmentNameFromCode(departmentFromSlid, t)
      : location.specialityShopName,
  };
};

export const useFilteredLocations = (
  itemNoAndType: ItemNoAndType | Loading | null,
  searchQuery: string,
  isFoodArticle = false,
  taskTypeId?: string
) => {
  const { t } = useTranslation();
  const itemNo =
    itemNoAndType && !isLoading(itemNoAndType) ? itemNoAndType.no : null;
  const type =
    itemNoAndType && !isLoading(itemNoAndType) ? itemNoAndType.type : null;

  const {
    data,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
  } = useQuery<GroupedSalesLocationsDTO>({
    queryKey: ["locations", itemNo, type, taskTypeId],
    queryFn: () => getLocations(itemNo!, type!, taskTypeId),
    enabled: !!itemNo && !!type,
    staleTime: INTERVAL.HOUR,
  });

  // Calculate combined total quantity before filtering
  const totalAssignedSalesSpaceQuantity = useMemo(() => {
    if (!data) return 0;
    const allLocations = [
      ...data.homeAndPrimaryLocations,
      ...data.otherSalesLocations,
    ];
    return calculateTotalQuantity(allLocations);
  }, [data]);

  const homeLocation = useMemo(() => {
    if (!data) return "";
    const home = data.homeAndPrimaryLocations.find((loc) => loc.isHome);
    return home?.slid ?? "";
  }, [data]);

  const filteredLocations = useMemo((): GroupedSalesLocations => {
    if (!data) {
      return {
        homeAndPrimaryLocations: [],
        otherSalesLocations: [],
        sgfLocations: [],
        customLocations: [],
      };
    }

    const query = searchQuery.trim().toLowerCase();

    const filteredCustomLocations = !query
      ? data.customLocations
      : data.customLocations.filter((location) =>
          location.toLowerCase().includes(query)
        );

    const hasNoSLMLocations =
      data.homeAndPrimaryLocations.length === 0 &&
      data.otherSalesLocations.length === 0 &&
      data.sgfLocations.length === 0;

    // Transform DTO to Domain, then filter
    const enrichedHomeAndPrimary = data.homeAndPrimaryLocations.map((loc) =>
      enrichLocation(loc, t)
    );

    const homeAndPrimaryLocations =
      isFoodArticle && hasNoSLMLocations
        ? [SFM_FALLBACK_LOCATION]
        : filterLocations(enrichedHomeAndPrimary, query);

    return {
      homeAndPrimaryLocations,
      otherSalesLocations: filterLocations(
        data.otherSalesLocations.map((loc) => enrichLocation(loc, t)),
        query
      ),
      sgfLocations: filterLocations(
        data.sgfLocations.map((loc) => enrichLocation(loc, t)),
        query
      ),
      customLocations: filteredCustomLocations,
    };
  }, [data, searchQuery, isFoodArticle, t]);

  return {
    filteredLocations,
    isLoadingLocations,
    isErrorLocations,
    totalAssignedSalesSpaceQuantity,
    homeLocation,
  };
};
